import { Fragment } from "react";
import { BriefcaseConveyorBelt, FolderCog, Trash2 } from "lucide-react";
import { Button } from "../../../../components/button";
import { NoDataPlaceholder } from "../../../../components/common";
import { ExpandCollapseButton } from "../../../../components/expand-collapse-button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../components/table";
import type { CampaignHierarchyItem } from "../../types";
import { CAMPAIGN_STATUS_LABELS } from "../../types/campaign-status.constants";
import { formatDate } from "../campaign.utils";
import { ProgramHierarchyTable } from "./ProgramHierarchyTable";

interface Props {
  campaigns: CampaignHierarchyItem[];
  expandedCampaignId: string | null;
  expandedProgramId: string | null;
  onToggleCampaign: (id: string) => void;
  onToggleProgram: (id: string) => void;
  onOpenCampaignDetail: (id: string) => void;
  onOpenProgramEditor: (id: string) => void;
  onOpenProgramDetail: (campaignId: string, programId: string) => void;
  onOpenOpportunityDetail: (campaignId: string, programId: string, opportunityId: string) => void;
  onDeleteCampaign: (id: string) => void;
}

export function CampaignHierarchyTable({
  campaigns,
  expandedCampaignId,
  expandedProgramId,
  onToggleCampaign,
  onToggleProgram,
  onOpenCampaignDetail,
  onOpenProgramEditor,
  onOpenProgramDetail,
  onOpenOpportunityDetail,
  onDeleteCampaign,
}: Props) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Open</TableHead>
          <TableHead>Campaign</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Programs</TableHead>
          <TableHead>Opportunities</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {campaigns.map((campaign) => {
          const expanded = expandedCampaignId === campaign.id;
          return (
            <Fragment key={campaign.id}>
              <TableRow key={campaign.id}>
                <TableCell>
                  <ExpandCollapseButton
                    expanded={expanded}
                    controls={`campaign-${campaign.id}`}
                    label={`Toggle ${campaign.name}`}
                    onClick={() => onToggleCampaign(campaign.id)}
                  />
                </TableCell>
                <TableCell>
                  <div className="font-medium">{campaign.name}</div>
                  <div className="text-xs text-[#6A7282]">
                    {campaign.description || "No description"}
                    {campaign.startDate && campaign.endDate
                      ? ` · ${formatDate(campaign.startDate)} - ${formatDate(campaign.endDate)}`
                      : ""}
                  </div>
                </TableCell>
                <TableCell>{CAMPAIGN_STATUS_LABELS[campaign.status]}</TableCell>
                <TableCell>{campaign.programCount}</TableCell>
                <TableCell>{campaign.opportunityCount}</TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onOpenProgramEditor(campaign.id)}
                    aria-label={`Edit programs for ${campaign.name}`}
                  >
                    <BriefcaseConveyorBelt className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onOpenCampaignDetail(campaign.id)}
                    aria-label={`Edit ${campaign.name}`}
                  >
                    <FolderCog className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onDeleteCampaign(campaign.id)}
                    aria-label={`Delete ${campaign.name}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
              {expanded ? (
                <TableRow id={`campaign-${campaign.id}`}>
                  <TableCell colSpan={6}>
                    <ProgramHierarchyTable
                      campaignId={campaign.id}
                      campaignName={campaign.name}
                      programs={campaign.programs}
                      expandedProgramId={expandedProgramId}
                      onToggleProgram={onToggleProgram}
                      onOpenProgramDetail={onOpenProgramDetail}
                      onOpenOpportunityDetail={onOpenOpportunityDetail}
                    />
                  </TableCell>
                </TableRow>
              ) : null}
            </Fragment>
          );
        })}
        {!campaigns.length ? (
          <TableRow>
            <TableCell colSpan={6}>
              <NoDataPlaceholder
                title="No campaigns found."
                description="No campaign matches the current filters."
              />
            </TableCell>
          </TableRow>
        ) : null}
      </TableBody>
    </Table>
  );
}
